import {
  Dismiss20Regular,
  Checkmark20Regular,
  ChevronDown20Regular,
  Calendar20Regular,
} from '@fluentui/react-icons';
import { useMemo, useState } from 'react';
import {
  formatBudgetStatusLabel,
  type BudgetStatus,
} from '../../orcamentos/orcamentosApi';
import {
  SearchableSelect,
  type SearchableSelectOption,
} from '../SearchableSelect';

interface FiltrarOrcamentosModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAplicarFiltros?: (filtros: FiltrosOrcamentos) => void;
  clientesDisponiveis?: string[];
  obrasDisponiveis?: string[];
}

export interface FiltrosOrcamentos {
  estados: BudgetStatus[];
  cliente: string;
  obra: string;
  dataCriacaoDe: string;
  dataCriacaoAte: string;
  valorMinimo: string;
  valorMaximo: string;
  prioridade: string;
}

export function FiltrarOrcamentosModal({ 
  isOpen, 
  onClose,
  onAplicarFiltros,
  clientesDisponiveis = [],
  obrasDisponiveis = [],
}: FiltrarOrcamentosModalProps) {
  const [estados, setEstados] = useState<BudgetStatus[]>([]);
  const [cliente, setCliente] = useState('');
  const [obra, setObra] = useState('');
  const [dataCriacaoDe, setDataCriacaoDe] = useState('');
  const [dataCriacaoAte, setDataCriacaoAte] = useState('');
  const [valorMinimo, setValorMinimo] = useState('');
  const [valorMaximo, setValorMaximo] = useState('');
  const [prioridade, setPrioridade] = useState('');

  const [isPrioridadeDropdownOpen, setIsPrioridadeDropdownOpen] = useState(false);

  // Derivadas dos orçamentos carregados (só clientes/obras com orçamentos) —
  // a fonte mantém-se; troca-se apenas o controlo. "" = sem filtro.
  const clienteOptions = useMemo<SearchableSelectOption<string>[]>(
    () => [
      { value: '', label: 'Todos os clientes' },
      ...clientesDisponiveis.map((c) => ({ value: c, label: c })),
    ],
    [clientesDisponiveis],
  );

  const obraOptions = useMemo<SearchableSelectOption<string>[]>(
    () => [
      { value: '', label: 'Todas as obras' },
      ...obrasDisponiveis.map((o) => ({ value: o, label: o })),
    ],
    [obrasDisponiveis],
  );

  const estadosDisponiveis: BudgetStatus[] = [
    'elaboracao',
    'aguardando',
    'pronto',
    'enviado',
    'aceite',
    'recusado',
  ];

  const prioridadesDisponiveis = [
    'Alta',
    'Normal',
    'Baixa'
  ];

  const toggleEstado = (estado: BudgetStatus) => {
    if (estados.includes(estado)) {
      setEstados(estados.filter(e => e !== estado));
    } else {
      setEstados([...estados, estado]);
    }
  };

  const getEstadoBadgeClass = (estado: BudgetStatus) => {
    switch (estado) {
      case 'elaboracao':
        return 'bg-chart-2/10 text-chart-2';
      case 'aguardando':
        return 'bg-chart-4/10 text-chart-4';
      case 'pronto':
        return 'bg-chart-3/10 text-chart-3';
      case 'enviado':
        return 'bg-chart-1/10 text-chart-1';
      case 'aceite':
        return 'bg-primary/10 text-primary';
      case 'recusado':
        return 'bg-muted text-muted-foreground';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const handleLimparFiltros = () => {
    setEstados([]);
    setCliente('');
    setObra('');
    setDataCriacaoDe('');
    setDataCriacaoAte('');
    setValorMinimo('');
    setValorMaximo('');
    setPrioridade('');
  };

  const handleAplicarFiltros = () => {
    const filtros: FiltrosOrcamentos = {
      estados,
      cliente,
      obra,
      dataCriacaoDe,
      dataCriacaoAte,
      valorMinimo,
      valorMaximo,
      prioridade
    };
    onAplicarFiltros?.(filtros);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div 
          className="bg-card border border-border shadow-lg w-full max-w-[560px] max-h-[90vh] flex flex-col"
          style={{ borderRadius: 'var(--radius-card)' }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="p-8 pb-6 border-b border-border">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h2 
                  className="text-foreground"
                  style={{ 
                    fontSize: 'var(--text-large)',
                    fontWeight: 'var(--font-weight-semi-bold)',
                    lineHeight: '1.3'
                  }}
                >
                  Filtrar Orçamentos
                </h2>
                <p 
                  className="text-secondary-text mt-2"
                  style={{ fontSize: 'var(--text-base)' }}
                >
                  Refine os resultados com os filtros abaixo.
                </p>
              </div>
              <button
                onClick={onClose}
                className="ml-4 p-2 hover:bg-accent rounded transition-colors"
                style={{ borderRadius: 'var(--radius)' }}
              >
                <Dismiss20Regular className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Body - Scrollable */}
          <div className="flex-1 overflow-y-auto p-8 space-y-8">
            {/* Secção 1: Estado do Orçamento */}
            <div className="space-y-4">
              <h3 
                className="text-foreground"
                style={{ 
                  fontSize: 'var(--text-section-title)',
                  fontWeight: 'var(--font-weight-semi-bold)'
                }}
              >
                Estado do Orçamento
              </h3>
              
              <div className="space-y-3">
                {estadosDisponiveis.map((estado) => (
                  <label key={estado} className="flex items-start gap-3 cursor-pointer group">
                    <div className="relative flex items-center justify-center mt-0.5">
                      <input
                        type="checkbox"
                        checked={estados.includes(estado)}
                        onChange={() => toggleEstado(estado)}
                        className="w-5 h-5 cursor-pointer appearance-none border-2 border-border rounded checked:bg-primary checked:border-primary transition-all"
                        style={{ borderRadius: 'var(--radius)' }}
                      />
                      {estados.includes(estado) && (
                        <Checkmark20Regular className="w-4 h-4 text-primary-foreground absolute pointer-events-none" />
                      )}
                    </div>
                    <span 
                      className={`inline-flex px-2 py-1 rounded ${getEstadoBadgeClass(estado)}`}
                      style={{ fontSize: 'var(--text-caption)', borderRadius: 'var(--radius)' }}
                    >
                      {formatBudgetStatusLabel(estado)}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            {/* Secção 2: Cliente */}
            <div className="space-y-2">
              <SearchableSelect<string>
                label="Cliente"
                labelClassName="text-foreground block"
                labelStyle={{
                  fontSize: 'var(--text-base)',
                  fontWeight: 'var(--font-weight-semi-bold)',
                }}
                value={cliente}
                onChange={(next) => setCliente(next ?? '')}
                options={clienteOptions}
                allowClear={false}
                searchPlaceholder="Pesquisar cliente..."
                emptyMessage="Nenhum cliente encontrado"
              />
            </div>

            {/* Secção 3: Obra */}
            <div className="space-y-2">
              <SearchableSelect<string>
                label="Obra"
                labelClassName="text-foreground block"
                labelStyle={{
                  fontSize: 'var(--text-base)',
                  fontWeight: 'var(--font-weight-semi-bold)',
                }}
                value={obra}
                onChange={(next) => setObra(next ?? '')}
                options={obraOptions}
                allowClear={false}
                searchPlaceholder="Pesquisar obra..."
                emptyMessage="Nenhuma obra encontrada"
              />
            </div>

            {/* Secção 4: Intervalo de Datas */}
            <div className="space-y-4">
              <h3 
                className="text-foreground"
                style={{ 
                  fontSize: 'var(--text-section-title)',
                  fontWeight: 'var(--font-weight-semi-bold)'
                }}
              >
                Intervalo de Datas
              </h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Data De */}
                <div className="space-y-2">
                  <label 
                    className="text-foreground block"
                    style={{ fontSize: 'var(--text-base)' }}
                  >
                    Data de Criação (de)
                  </label>
                  <div className="relative">
                    <input
                      type="date"
                      value={dataCriacaoDe}
                      onChange={(e) => setDataCriacaoDe(e.target.value)}
                      className="w-full px-4 py-2.5 bg-background border border-border rounded hover:border-primary/50 transition-colors min-h-[44px]"
                      style={{ 
                        borderRadius: 'var(--radius-button)',
                        fontSize: 'var(--text-base)'
                      }}
                    />
                    <Calendar20Regular className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-secondary-text pointer-events-none" />
                  </div>
                </div>

                {/* Data Até */}
                <div className="space-y-2">
                  <label 
                    className="text-foreground block"
                    style={{ fontSize: 'var(--text-base)' }}
                  >
                    Data de Criação (até)
                  </label>
                  <div className="relative">
                    <input
                      type="date"
                      value={dataCriacaoAte}
                      onChange={(e) => setDataCriacaoAte(e.target.value)}
                      className="w-full px-4 py-2.5 bg-background border border-border rounded hover:border-primary/50 transition-colors min-h-[44px]"
                      style={{ 
                        borderRadius: 'var(--radius-button)',
                        fontSize: 'var(--text-base)'
                      }}
                    />
                    <Calendar20Regular className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-secondary-text pointer-events-none" />
                  </div>
                </div>
              </div>
            </div>

            {/* Secção 5: Valor Estimado */}
            <div className="space-y-4">
              <h3 
                className="text-foreground"
                style={{ 
                  fontSize: 'var(--text-section-title)',
                  fontWeight: 'var(--font-weight-semi-bold)'
                }}
              >
                Valor Estimado
              </h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Valor Mínimo */}
                <div className="space-y-2">
                  <label 
                    className="text-foreground block"
                    style={{ fontSize: 'var(--text-base)' }}
                  >
                    Valor mínimo (€)
                  </label>
                  <input
                    type="number"
                    value={valorMinimo}
                    onChange={(e) => setValorMinimo(e.target.value)}
                    placeholder="0,00"
                    min="0"
                    step="0.01"
                    className="w-full px-4 py-2.5 bg-background border border-border rounded hover:border-primary/50 transition-colors min-h-[44px]"
                    style={{ 
                      borderRadius: 'var(--radius-button)',
                      fontSize: 'var(--text-base)'
                    }}
                  />
                </div>

                {/* Valor Máximo */}
                <div className="space-y-2">
                  <label 
                    className="text-foreground block"
                    style={{ fontSize: 'var(--text-base)' }}
                  >
                    Valor máximo (€)
                  </label>
                  <input
                    type="number"
                    value={valorMaximo}
                    onChange={(e) => setValorMaximo(e.target.value)}
                    placeholder="0,00"
                    min="0"
                    step="0.01"
                    className="w-full px-4 py-2.5 bg-background border border-border rounded hover:border-primary/50 transition-colors min-h-[44px]"
                    style={{ 
                      borderRadius: 'var(--radius-button)',
                      fontSize: 'var(--text-base)'
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Secção 6: Prioridade */}
            <div className="space-y-2">
              <label 
                className="text-foreground block"
                style={{ 
                  fontSize: 'var(--text-base)',
                  fontWeight: 'var(--font-weight-semi-bold)'
                }}
              >
                Prioridade
              </label>

              <div className="relative">
                <button
                  onClick={() => setIsPrioridadeDropdownOpen(!isPrioridadeDropdownOpen)}
                  className="w-full px-4 py-2.5 bg-background border border-border rounded text-left flex items-center justify-between hover:border-primary/50 transition-colors min-h-[44px]"
                  style={{ borderRadius: 'var(--radius-button)' }}
                >
                  <span 
                    className={prioridade ? 'text-foreground' : 'text-secondary-text'}
                    style={{ fontSize: 'var(--text-base)' }}
                  >
                    {prioridade || 'Selecione uma prioridade…'}
                  </span>
                  <ChevronDown20Regular className="w-5 h-5 text-secondary-text" />
                </button>

                {isPrioridadeDropdownOpen && (
                  <>
                    <div 
                      className="fixed inset-0 z-[60]" 
                      onClick={() => setIsPrioridadeDropdownOpen(false)}
                    />
                    
                    <div 
                      className="absolute left-0 right-0 bottom-full mb-2 bg-card border border-border shadow-lg z-[70]"
                      style={{ borderRadius: 'var(--radius-card)' }}
                    >
                      <div className="py-2">
                        <button
                          onClick={() => {
                            setPrioridade('');
                            setIsPrioridadeDropdownOpen(false);
                          }}
                          className={`w-full px-4 py-2.5 text-left hover:bg-accent transition-colors ${
                            !prioridade ? 'bg-accent' : ''
                          }`}
                          style={{ fontSize: 'var(--text-base)' }}
                        >
                          <span className="text-secondary-text">Todas as prioridades</span>
                        </button>
                        {prioridadesDisponiveis.map((p) => (
                          <button
                            key={p}
                            onClick={() => {
                              setPrioridade(p);
                              setIsPrioridadeDropdownOpen(false);
                            }}
                            className={`w-full px-4 py-2.5 text-left hover:bg-accent transition-colors ${
                              prioridade === p ? 'bg-accent' : ''
                            }`}
                            style={{ fontSize: 'var(--text-base)' }}
                          >
                            {p}
                          </button>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Footer - Sticky */}
          <div className="p-8 pt-6 border-t border-border bg-card">
            <div className="flex items-center justify-between gap-3">
              {/* Left Side */}
              <button
                onClick={handleLimparFiltros}
                className="px-4 py-2 text-secondary-text hover:text-foreground hover:bg-accent border border-transparent rounded transition-colors min-h-[44px]"
                style={{ 
                  borderRadius: 'var(--radius-button)',
                  fontSize: 'var(--text-base)'
                }}
              >
                Limpar Filtros
              </button>

              {/* Right Side */}
              <div className="flex items-center gap-3">
                <button
                  onClick={onClose}
                  className="px-4 py-2 bg-secondary hover:bg-accent border border-border rounded transition-colors min-h-[44px]"
                  style={{ 
                    borderRadius: 'var(--radius-button)',
                    fontSize: 'var(--text-base)'
                  }}
                >
                  Cancelar
                </button>
                <button
                  onClick={handleAplicarFiltros}
                  className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded transition-colors min-h-[44px]"
                  style={{ 
                    borderRadius: 'var(--radius-button)',
                    fontSize: 'var(--text-base)'
                  }}
                >
                  Aplicar Filtros
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
